import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useFormChanged, zodResolver } from "@/lib/form";
import { generatePassphrase } from "@/lib/passphrase";
import { useCreateEndpoint, useDomains, useUpdateEndpoint } from "@/lib/queries";
import type { Endpoint } from "@/lib/types";

/**
 * Create / edit a SIP endpoint.
 *
 * The form is deliberately narrow. `sip_users.json_data` can carry any FreeSWITCH param or
 * variable, but exposing that as a raw JSON editor in the main path invites someone to delete
 * `jsonrpc-allowed-methods` and silently break the console phone. The server rebuilds the
 * document from these fields and preserves the Verto allowances; the raw escape hatch stays on
 * the API for the rare case that needs it.
 *
 * **An endpoint somebody owns is narrower still.** Its display name is that person's name, its
 * extension is theirs to choose, and the record toggle is theirs — all three are edited on the
 * user and the server refuses them here (ADR-012 in `platform`). What is left is the
 * operational half: the SIP password and whether the endpoint is in the directory at all. Those
 * stay because a rep whose registration is broken cannot fix it from inside a console they can
 * no longer sign a phone into.
 */

const schema = z.object({
  // Same character class the server enforces — these end up in an XML attribute in the
  // FreeSWITCH directory document.
  extension: z
    .string()
    .min(1, "Required")
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/, "Letters, digits, dot, underscore and hyphen only"),
  domain_id: z.number({ message: "Choose a domain" }),
  display_name: z.string().max(200).optional(),
  caller_id_number: z.string().max(32).optional(),
  number_alias: z.string().max(64).optional(),
  password: z.string().optional(),
  enabled: z.boolean(),
  record_calls: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function EndpointFormDialog({
  open, onOpenChange, endpoint, defaultDomainId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  endpoint?: Endpoint;
  defaultDomainId?: number;
}) {
  const editing = Boolean(endpoint);
  const owner = endpoint?.assigned_user_name ?? null;
  const toast = useToast();
  const create = useCreateEndpoint();
  const update = useUpdateEndpoint();
  const { data: domains } = useDomains();
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      extension: "", domain_id: defaultDomainId ?? (undefined as unknown as number),
      display_name: "", caller_id_number: "", number_alias: "",
      password: "", enabled: true, record_calls: true,
    },
  });
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      endpoint
        ? {
            extension: endpoint.extension,
            domain_id: endpoint.domain_id,
            display_name: endpoint.display_name ?? "",
            caller_id_number: endpoint.caller_id_number ?? "",
            number_alias: endpoint.number_alias ?? "",
            password: "",
            enabled: endpoint.enabled,
            record_calls: endpoint.record_calls,
          }
        : {
            extension: "",
            domain_id: (defaultDomainId ?? domains?.[0]?.id) as number,
            display_name: "", caller_id_number: "", number_alias: "",
            // Generated up front and shown: the person creating it has to put it into a desk
            // phone or hand it over, so a blank field they must remember to fill is a trap.
            password: generatePassphrase(3),
            enabled: true,
            // On, matching the server's default. A form that offered it off would quietly make
            // "recording is the norm" false for every endpoint made through the console.
            record_calls: true,
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endpoint, defaultDomainId, domains]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const payload = {
      extension: values.extension,
      domain_id: values.domain_id,
      display_name: values.display_name?.trim() || null,
      caller_id_number: values.caller_id_number?.trim() || null,
      number_alias: values.number_alias?.trim() || null,
      enabled: values.enabled,
      record_calls: values.record_calls,
      ...(values.password ? { password: values.password } : {}),
    };
    try {
      if (endpoint) {
        // `extension` and `domain_id` are the endpoint's identity on the switch, and a device's
        // is fixed once created — a person's is renamed on their user record instead.
        const { extension: _e, domain_id: _d, ...editable } = payload;
        // The owner's three fields are not merely disabled in the form: sending them at all is
        // a 409, and a disabled input still round-trips its value through `payload`.
        if (owner) {
          delete (editable as Partial<typeof editable>).display_name;
          delete (editable as Partial<typeof editable>).number_alias;
          delete (editable as Partial<typeof editable>).record_calls;
        }
        await update.mutateAsync({ id: endpoint.id, ...editable });
        toast.success("Endpoint updated", `${endpoint.extension}@${endpoint.domain_name}`);
      } else {
        const created = await create.mutateAsync(payload);
        toast.success("Endpoint created", `${created.extension}@${created.domain_name}`);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the endpoint.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{editing ? "Edit endpoint" : "New endpoint"}</DialogTitle>
          <DialogDescription>
            {owner
              ? "Its owner's half is edited on their user record; the switch-side half is here."
              : "A device registration on the switch that belongs to nobody — a shared handset, a door intercom. A person's phone is made with their account."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            {owner && (
              <Callout tone="info">
                This is {owner}&rsquo;s phone. Their name, extension and recording setting are
                edited on their user record — what you can change here is the SIP password and
                whether the endpoint is in the directory at all.
              </Callout>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Extension"
                htmlFor="extension"
                required
                hint={
                  owner
                    ? `${owner} chooses this, on their user record or in their own settings.`
                    : editing
                      ? "Fixed once created."
                      : "What they authenticate as."
                }
                error={form.formState.errors.extension?.message}
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  disabled={editing}
                  placeholder="1001"
                  invalid={Boolean(form.formState.errors.extension)}
                  {...form.register("extension")}
                />
              </Field>

              <Field
                label="Domain"
                htmlFor="domain_id"
                required
                hint={editing ? "Fixed once created." : "The SIP realm it registers in."}
                error={form.formState.errors.domain_id?.message}
              >
                <Select
                  value={form.watch("domain_id")?.toString() ?? ""}
                  onValueChange={(v) => form.setValue("domain_id", Number(v), { shouldValidate: true })}
                  disabled={editing}
                >
                  <SelectTrigger invalid={Boolean(form.formState.errors.domain_id)}>
                    <SelectValue placeholder="Choose a domain" />
                  </SelectTrigger>
                  <SelectContent>
                    {(domains ?? []).map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field
              label="Display name"
              htmlFor="display_name"
              hint={
                owner
                  ? `Always ${owner}'s name — change it on their user record.`
                  : "The caller-id name on calls from this endpoint."
              }
            >
              <Input
                autoComplete="off"
                placeholder="Rep One"
                disabled={Boolean(owner)}
                {...form.register("display_name")}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Caller ID number"
                htmlFor="caller_id_number"
                hint="Defaults to the extension."
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  placeholder="+13105550142"
                  {...form.register("caller_id_number")}
                />
              </Field>
              <Field
                label="Number alias"
                htmlFor="number_alias"
                hint={
                  owner
                    ? "Not used on a person's phone — they answer to one number, their extension."
                    : "A short extension to dial, if it differs from the auth id."
                }
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  disabled={Boolean(owner)}
                  {...form.register("number_alias")}
                />
              </Field>
            </div>

            <Field
              label={editing ? "Set a new SIP password" : "SIP password"}
              htmlFor="password"
              hint={
                editing
                  ? "Leave blank to keep the current one. Changing it forces the device to re-register."
                  : "The device authenticates with this. Stored as FreeSWITCH needs it."
              }
              error={form.formState.errors.password?.message}
              action={
                <button
                  type="button"
                  className="text-xs text-accent-11 hover:underline"
                  onClick={() => form.setValue("password", generatePassphrase(3))}
                >
                  Generate
                </button>
              }
            >
              <Input type="text" autoComplete="off" className="font-mono" {...form.register("password")} />
            </Field>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-xs font-medium text-text">Enabled</p>
                <p className="text-xs text-text-muted">
                  A disabled endpoint is removed from the directory and cannot register.
                </p>
              </div>
              <Switch
                checked={form.watch("enabled")}
                onCheckedChange={(v) => form.setValue("enabled", v)}
                aria-label="Enabled"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-xs font-medium text-text">Record calls</p>
                <p className="text-xs text-text-muted">
                  {/* Both directions, because that is the part people get wrong: the toggle is
                      not "record what they dial", it is "record this phone". */}
                  {owner
                    ? `${owner} owns this toggle — set it on their user record, or they can set it themselves.`
                    : "Records this phone's own side of calls it places and calls it is rung for. Takes effect on the next call."}
                </p>
              </div>
              <Switch
                checked={form.watch("record_calls")}
                onCheckedChange={(v) => form.setValue("record_calls", v)}
                disabled={Boolean(owner)}
                aria-label="Record calls"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {editing ? "Save changes" : "Create endpoint"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
